import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef } from 'react'

import { authorizationQrWaitCreateMutation } from 'src/api/main'
import {
  parseQrLoginWaitResponse,
  type QrLoginWaitResponse
} from 'src/features/auth/api/qrLogin'

import { QR_LOGIN_WAIT_STATUS, QR_PANEL_STATUS } from '../constants'
import type {
  QrPanelState,
  QrSignInPanelProps,
  SetQrPanelState
} from '../types'
import { isWaitingStatus } from '../utils'

type UseQrWaitPollingParams = {
  state: QrPanelState
  stateRef: { current: QrPanelState }
  refetchTimeoutRef: { current: ReturnType<typeof setTimeout> | null }
  handledApprovalRef: { current: boolean }
  setState: SetQrPanelState
  setExpiresAtMs: (value: number) => void
  showApiErrors: (error: unknown) => void
  onAuthenticated: QrSignInPanelProps['onAuthenticated']
}

export const useQrWaitPolling = ({
  state,
  stateRef,
  refetchTimeoutRef,
  handledApprovalRef,
  setState,
  setExpiresAtMs,
  showApiErrors,
  onAuthenticated
}: UseQrWaitPollingParams) => {
  const { mutate: waitMutate } = useMutation(
    authorizationQrWaitCreateMutation()
  )
  const polledSessionRef = useRef<string | null>(null)
  const runWaitRef = useRef<() => void>(() => {})

  const scheduleNextWait = useCallback(
    (pollIntervalSeconds: number) => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current)
      }
      refetchTimeoutRef.current = setTimeout(() => {
        runWaitRef.current()
      }, pollIntervalSeconds * 1000)
    },
    [refetchTimeoutRef]
  )

  const applyWaitResponse = useCallback(
    async (response: QrLoginWaitResponse, sessionId: string) => {
      const current = stateRef.current
      if (!current.session || current.session.session_id !== sessionId) {
        return
      }

      if (
        response.status === QR_LOGIN_WAIT_STATUS.PENDING ||
        response.status === QR_LOGIN_WAIT_STATUS.SCANNED
      ) {
        setState({
          status:
            response.status === QR_LOGIN_WAIT_STATUS.PENDING
              ? QR_PANEL_STATUS.ACTIVE
              : QR_PANEL_STATUS.SCANNED,
          session: current.session
        })
        setExpiresAtMs(Date.now() + response.expires_in_remaining * 1000)
        scheduleNextWait(current.session.poll_interval)
        return
      }

      if (response.status === QR_LOGIN_WAIT_STATUS.APPROVED) {
        if (handledApprovalRef.current) {
          return
        }
        handledApprovalRef.current = true
        setState({ status: QR_PANEL_STATUS.APPROVED, session: current.session })
        await onAuthenticated(response)
        return
      }

      setState({ status: response.status, session: current.session })
    },
    [
      handledApprovalRef,
      onAuthenticated,
      scheduleNextWait,
      setExpiresAtMs,
      setState,
      stateRef
    ]
  )

  const handleWaitError = useCallback(
    (error: unknown, sessionId: string) => {
      const current = stateRef.current
      if (current.session?.session_id !== sessionId) {
        return
      }

      setState((previous) => ({
        status: QR_PANEL_STATUS.ERROR,
        session: previous.session
      }))
      showApiErrors(error)
    },
    [setState, showApiErrors, stateRef]
  )

  const runWait = useCallback(() => {
    const current = stateRef.current
    if (!current.session) {
      return
    }

    const sessionId = current.session.session_id

    waitMutate(
      {
        body: {
          session_id: sessionId,
          web_token: current.session.web_token
        }
      },
      {
        onSuccess: (data) =>
          applyWaitResponse(parseQrLoginWaitResponse(data), sessionId),
        onError: (error) => handleWaitError(error, sessionId)
      }
    )
  }, [applyWaitResponse, handleWaitError, stateRef, waitMutate])

  useEffect(() => {
    runWaitRef.current = runWait
  }, [runWait])

  useEffect(() => {
    if (!state.session || !isWaitingStatus(state.status)) {
      return
    }

    if (polledSessionRef.current === state.session.session_id) {
      return
    }

    polledSessionRef.current = state.session.session_id
    runWait()
  }, [runWait, state.session, state.status])
}
